import { useEffect } from 'react';
import { StaffAsyncSelect } from '@/pages/POS/components/StaffAsyncSelect';
import { useAuth } from '@/hooks/useAuth';
import { useRbac } from '@/hooks/useRbac';
import { isB2BSalesUser } from '@/utils/salesManagerRoles';
import type { PosStaffSalesChannel } from '@/pages/POS/types';

interface Props {
  value?: string;
  onChange: (staffId: string | undefined) => void;
  onChangeWithLabel?: (staffId: string | undefined, name: string) => void;
  storeId?: string;
  /**
   * TCR-2429 — `'b2b'` khi đang tạo đơn B2B: danh sách đổi sang NVKD GT/MT toàn công ty và
   * ô này mang nghĩa "NVKD ghi nhận doanh số". Bỏ trống = hành vi cũ, không đổi gì.
   */
  salesChannel?: PosStaffSalesChannel;
  placeholder?: string;
  status?: 'error' | 'warning';
  initialLabel?: string;
  disabled?: boolean;
}

/**
 * TCR-1705 — thiếu quyền `pos.care_staff_reassign` thì ô này khoá cứng về đúng tài khoản đang
 * đăng nhập.
 *
 * Quyền đó vốn là một "quyền ma": có trong catalogue nhưng chưa nối vào route hay logic nào, nên
 * bỏ tick trong màn Vai trò & Quyền không có tác dụng gì — QA vẫn đổi được nhân viên chăm sóc
 * sang người khác. Nay chặn ở CẢ hai phía: `PosCareStaffGuard` ở BE (đường gọi API trực tiếp) và
 * ô nhập này ở FE (đường qua giao diện).
 *
 * Đặt lock ở ĐÂY chứ không ở từng nơi gọi, vì cả ba màn dùng chung component này đều phải khoá
 * theo yêu cầu ticket: POS tạo đơn (`POS/index.tsx`), Sửa đơn (`Orders/edit.tsx`) và modal đổi
 * nhân viên ở trang chi tiết đơn. Gom về một chỗ thì không màn nào sót.
 *
 * `disabled` truyền từ ngoài vào vẫn được tôn trọng (OR chứ không ghi đè): `Orders/edit.tsx` đã
 * tự khoá field này theo `isCareStaffEditable` vì lý do khác (đơn chỉ được sửa phần giao vận),
 * lock quyền không được phép mở lại thứ màn đó đang cố đóng.
 *
 * TCR-2429 — thêm nhánh B2B: trên đơn B2B ô này là NGƯỜI ĐƯỢC GHI NHẬN DOANH SỐ, và chỉ NVKD
 * GT/MT mới hợp lệ. Tài khoản đang đăng nhập không phải NVKD thì KHÔNG được lấy chính mình làm
 * giá trị (BE sẽ ghi doanh số cho kế toán/quản lý — sai người, và im lặng), nên giá trị bị xoá
 * để rule required buộc chọn đúng NVKD; nếu tài khoản đó còn thiếu quyền đổi người thì hiện
 * luôn một dòng cảnh báo thay vì để ô trống không giải thích.
 */
export function CareStaffAsyncSelect({
  value,
  onChange,
  onChangeWithLabel,
  storeId,
  salesChannel,
  placeholder = 'Chọn nhân viên chăm sóc',
  status,
  initialLabel,
  disabled,
}: Props) {
  const { user } = useAuth();
  const { hasPermission } = useRbac();
  const locked = !hasPermission('pos.care_staff_reassign');
  const selfId = user?.id;
  const isB2BMode = salesChannel === 'b2b';
  // Chỉ tính trên tài khoản CÓ id: thiếu id thì không có giá trị nào để mà sai.
  const selfIsNotB2BSales = isB2BMode && !!selfId && !isB2BSalesUser(user);
  // Khoá quyền ⇒ không có đường đổi người, mà chính mình lại không hợp lệ ⇒ để trống + cảnh báo.
  const blockedSelf = selfIsNotB2BSales && locked;
  // Đơn B2B nạp từ snapshot cũ (tạo trước TCR-2429) có thể đang mang chính mình dù không phải
  // NVKD — giá trị đó sai với chế độ hiện tại nên cũng phải xoá, kể cả khi có quyền đổi người.
  const staleSelfValue = selfIsNotB2BSales && value === selfId;
  const mustClear = blockedSelf || staleSelfValue;
  const effectiveValue = mustClear ? undefined : locked && selfId ? selfId : value;

  // Ép giá trị về chính mình khi đang khoá (hoặc xoá khi chính mình không hợp lệ với đơn B2B).
  // Không chỉ hiển thị: field này nằm trong antd Form, nếu chỉ đổi `value` hiển thị mà không
  // báo ngược lên form thì lúc submit vẫn gửi đi `care_staff_id` cũ (của người khác) — đúng thứ
  // ticket muốn chặn. Cũng bao luôn trường hợp mở một đơn cũ đang gán người khác: form được sửa
  // về chính mình ngay khi mở.
  useEffect(() => {
    if (mustClear) {
      if (value === undefined) return;
      if (onChangeWithLabel) onChangeWithLabel(undefined, '');
      else onChange(undefined);
      return;
    }
    if (!locked || !selfId || value === selfId) return;
    if (onChangeWithLabel) onChangeWithLabel(selfId, user?.name ?? '');
    else onChange(selfId);
  }, [mustClear, locked, selfId, value, onChange, onChangeWithLabel, user?.name]);

  return (
    <>
      <StaffAsyncSelect
        value={effectiveValue}
        onChange={onChange}
        onChangeWithLabel={onChangeWithLabel}
        storeId={storeId}
        salesChannel={salesChannel}
        placeholder={placeholder}
        warning={status === 'warning'}
        // Khi khoá, `StaffAsyncSelect` không fetch được danh sách để tự resolve nhãn (nó disabled),
        // nên đưa thẳng tên người đăng nhập vào làm nhãn hiển thị.
        initialLabel={locked && !mustClear ? (user?.name ?? initialLabel) : initialLabel}
        disabled={disabled || locked}
      />
      {blockedSelf && (
        <span style={{ color: '#d46b08', fontSize: 11, lineHeight: 1.3 }}>
          Tài khoản của bạn không phải NVKD nên không thể ghi nhận doanh số đơn B2B. Cần quyền
          “Đổi nhân viên chăm sóc” hoặc dùng tài khoản NVKD để tạo đơn này.
        </span>
      )}
    </>
  );
}
